import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import AddEditTrunkModal from './AddEditTrunkModal';
import TestCallModal from './TestCallModal';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  trunkDelete,
  trunkListGet,
  trunkStatusAllGet,
  useTrunkList
} from '../../../store/slices/tenantSlices/configurationSlice';
import { checkModulePermission } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';

// Row colour carries meaning, not decoration: it is the trunk's LIVE
// registration / reachability state from FreeSWITCH.
//   green  registered, or reachable on an IP-authenticated trunk
//   amber  reachable but not registered - usually bad credentials
//   red    unreachable, backing off, or not loaded in FreeSWITCH
const LIVE_STATE = {
  connected: { tint: 'rgba(44, 208, 126, 0.12)', color: '#1a9c5b', text: 'Registered' },
  not_connected: { tint: 'rgba(255, 193, 7, 0.14)', color: '#b98900', text: 'Not Registered' },
  down: { tint: 'rgba(246, 45, 81, 0.10)', color: '#d12944', text: 'Down' },
  not_provisioned: { tint: 'rgba(246, 45, 81, 0.10)', color: '#d12944', text: 'Not Loaded' },
  unknown: { tint: 'transparent', color: '#6c757d', text: 'Unknown' }
};

const Trunk = () => {
  const dispatch = useDispatch();
  const trunkList = useTrunkList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [testTrunk, setTestTrunk] = useState(null);
  // Live registration state per trunk id, from FreeSWITCH. The `status` column
  // on the row is only the admin enable/disable flag and says nothing about
  // whether the trunk is actually registered or reachable.
  const [liveStatus, setLiveStatus] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  // One request covers every trunk, so this costs the same whether the page
  // shows 10 rows or 100.
  const fetchLiveStatus = async () => {
    try {
      const res = await dispatch(trunkStatusAllGet()).unwrap();
      setLiveStatus(res?.by_id || {});
    } catch (error) {
      // A trunk list that cannot reach FreeSWITCH should still render; rows
      // simply fall back to no colour rather than showing a wrong one.
      console.log('error', error);
      setLiveStatus({});
    }
  };

  const fetchTrunkList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(trunkListGet(payload)).unwrap();
      fetchLiveStatus();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteBreak = async () => {
    try {
      const response = await dispatch(trunkDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (trunkList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchTrunkList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchTrunkList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Trunk"
        isExpandSearch
        isAdd
        isAddDisabled={!checkModulePermission('is_add', 'Trunks', userRolePermission)}
        onAddClick={() => setOpenAddModal(true)}
        addBtnName="Add Trunk"
        searchPlaceholder="Search Trunk"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : trunkList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Trunks', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Register Type</th>
                  <th>Domain</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {trunkList?.data?.map((val, index) => {
                  const live = liveStatus[val?.id];
                  const ui = LIVE_STATE[live?.state] || LIVE_STATE.unknown;
                  return (
                    <tr
                      key={index}
                      style={{ backgroundColor: ui.tint }}
                      title={live?.detail || ''}>
                      <td>{val?.name}</td>
                      <td>{val?.register_type}</td>
                      <td>{val?.domain}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: ui.color,
                              flexShrink: 0
                            }}
                          />
                          {/* Text as well as colour, so the state is readable
                              without relying on colour vision. */}
                          <span style={{ color: ui.color, fontWeight: 600 }}>
                            {live ? ui.text : '—'}
                          </span>
                          {val?.status === false && (
                            <span className="text-muted" style={{ fontSize: '11px' }}>
                              (disabled)
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          {/* Test Call sits before Edit. Gated on is_edit because
                              placing a real call is a change-level action. */}
                          <div
                            className={`cursor-pointer text-success ${
                              !checkModulePermission('is_edit', 'Trunks', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}
                            title="Test Call">
                            <Icon.PhoneCall
                              size={16}
                              onClick={() => {
                                if (checkModulePermission('is_edit', 'Trunks', userRolePermission)) {
                                  setTestTrunk(val);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Trunks', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_edit', 'Trunks', userRolePermission)
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', 'Trunks', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_delete', 'Trunks', userRolePermission)
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={trunkList?.total}
              limit={trunkList?.limit}
              page={trunkList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => {
                fetchTrunkList();
                fetchLiveStatus();
              }}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteBreak()}
      />
      <AddEditTrunkModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchTrunkList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
      <TestCallModal
        isOpen={!!testTrunk}
        trunk={testTrunk}
        onCloseClick={() => setTestTrunk(null)}
      />
    </div>
  );
};

export default Trunk;
